// The workspace "cards engine": turns SAM actions (and replayed/restored views) into a `cards`
// message that UXView renders, and maintains the per-session view history (the "⋯" menu).
//
// It owns: openCards (which detail is expanded per module) and the row caches
// (cardRecordsRef / lastFilterRef). It wires `useSamActions` (the socket→callback bridge) and the
// reload-restore hydrate effect, and auto-scrolls the thread as messages arrive.
//
// Messaging (send / decisions / record edits) lives in `useChatMessaging` — this hook is read-only
// with respect to text bubbles except for the confirm/action cards SAM pushes.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { socketService } from '@/services/SocketService';
import { useAppDispatch } from '@/hooks';
import { workspaceApi } from '@/redux/internal-services/workspaceApi';
import { quickQuotesApi } from '@/redux/internal-services/quickQuotesApi';
import { proposalsApi } from '@/redux/internal-services/proposalsApi';
import { notesApi } from '@/redux/internal-services/notesApi';
import { workflowEngineApi } from '@/redux/internal-services/workflowEngineApi';
import { pushToast } from '@/redux/slices/toastSlice';
import { useSamActions } from '@/pages/chat/hooks/useSamActions';
import { useSocketStatus } from '@/pages/chat/hooks/useSocketStatus';
import type { ProposalSurfaceInteractPayload, SamCardEvent, SamFacet, SamModule } from '@/components/SAM/samActionTypes';
import { cardId, cardInfo, sv, type Rec } from '@/components/chat/ChatPills';
import { getVisibleRows, type VisibleRow } from '@/components/chat/visibleListRows';
import type { CardsMessage } from '@/components/chat/UXView';
import { contextRefFor, viewLabel, recentViewKey, CONTEXT_REF_TYPE, type RecentEntry } from '@/components/chat/recentViews';
import type { RecordViewInput } from '@/pages/chat/hooks/useRecentViews';
import { setSelectedUser, setMeetingFilter, type WorkspaceView } from '@/redux/slices/uiContextSlice';
import { navigateCalendarAction } from '@/redux/slices/calendarNavSlice';
import { useCardCatalogue, useCardContextFields, useCardEventRouting, useModuleTools } from '@/components/SAM/moduleTools';
import {
  buildCardContext, matchingCardEvent, resolveCardDefinition, type CardContext,
} from '@/components/SAM/cards/cardContext';
import type { ActionCardMessage } from '@/components/chat/ActionCard';
import type { ConfirmMessage } from '@/components/chat/ConfirmCard';
import type { ComposeDraftInput, DraftCardMessage, DraftSnapshot } from '@/components/Communications/ComposeCard';
import type { NoteCardMessage } from '@/components/chat/NotePanel';
import type { WorkflowCardMessage } from '@/components/Workflow/WorkflowPanel';
import type { ChatMessage, Mode } from '../types';
import type { SamSurfaceEmit } from '@/hooks/useSamSurfaceContext';

interface UseChatWorkspaceOptions {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  conversationId: string;
  mode: Mode;
  isDesktop: boolean;
  /** Platform admins read users/roles cross-tenant from different REST endpoints. */
  isPlatform: boolean;
  /** Whether SAM action cards are also rebuilt inline in the chat thread (config-gated). */
  inlineActionCards: boolean;
  threadRef: React.RefObject<HTMLDivElement | null>;
  /** Mobile: hide the chat thread so freshly-rendered cards take over the screen. */
  setChatOn: React.Dispatch<React.SetStateAction<boolean>>;
  /** Reset the hands-free card index when a new set of cards is rendered. */
  setHfIndex: React.Dispatch<React.SetStateAction<number>>;
  /** Current hands-free card index — navigate_cards steps from it. */
  hfIndex: number;
  /** navigate_cards landed on one engagement row of the engagement list — show that engagement. */
  onPickEngagement?: (pick: { propertyId: string; engagementId: string }) => void;
  /** SAM start_note_taker tool — the note-capture card opened in the workspace; ChatPage brings the
   *  workspace surface into view (and mic muting happens in NotePanel's capture callbacks). */
  onOpenNoteTaker?: (opts?: { contextType?: string; entityId?: string }) => void;
  /** SAM execute_note_followups tool — trigger "Create all selected" on the open note review card. */
  onExecuteNoteFollowups?: () => void;
  /** SAM set_note_followup_included tool — check/uncheck one, several, or all follow-up
   *  suggestions on the open note review card. */
  onSetNoteFollowupIncluded?: (opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => void;
  /** SAM open_note_followup tool — expand a follow-up's review/edit panel, optionally
   *  prefilling fields, without creating anything. */
  onExpandNoteFollowup?: (opts: { suggestionId: string; fields?: Record<string, unknown> }) => void;
  /** When true, the notifications panel overlay is visible — workspaceView should report an
   *  empty notifications surface so SAM doesn't see stale module items in its context. */
  openNotificationsPanel?: boolean;
  /** Add a view to the workspace "⋯" recent-views menu (`useRecentViews.record`). Every surface
   *  this hook opens funnels through it, so the menu has one writer instead of five. */
  recordView: (input: RecordViewInput) => void;
  /** Fired the moment a replayed "⋯" view is committed to the workspace. ChatPage drops the
   *  overlays it renders in front of the latest surface (a notification-opened engagement or
   *  approval, the bell's notifications panel) — otherwise the replayed view lands behind them
   *  and the pick looks like a no-op until a reload clears the overlay. */
  onViewReplayed?: () => void;
}

export interface ChatWorkspace {
  openCards: Partial<Record<SamModule, string | null>>;
  setOpenCards: React.Dispatch<React.SetStateAction<Partial<Record<SamModule, string | null>>>>;
  /** True when the property detail was opened by SAM (after create/open) rather than by a user
   *  list-click — controls whether WorkspacePropertyDetail shows its back button. */
  /** Navigation source for the currently-open entity detail per module.
   *  'list'  = user clicked an item in that module's list → show Back button.
   *  'agent' = SAM opened the detail programmatically → hide Back button.
   *  'recent'= user re-opened via the ⋯ history menu → hide Back button.
   *  Absent/undefined when no detail is open. */
  openCardSources: Partial<Record<SamModule, 'list' | 'agent' | 'recent'>>;
  /** Re-run a saved view from the "⋯" menu (re-fetch + bring its cards to the front). */
  replayView: (entry: RecentEntry) => Promise<void>;
  /** "View" on an inline action card: (re-)open the workspace panel for a module + filter. */
  openActionView: (module: SamModule, filter: Rec, title?: string, caption?: string, recordId?: string | null) => Promise<void>;
  /** Structured view of the latest visible workspace surface (cards OR draft; null when none) —
   *  ChatPage registers it as a visible surface when the workspace panel is actually on screen. */
  workspaceView: WorkspaceView | null;
  /** Mark the focused/hands-free user as the agent's rename target (or null to clear). */
  selectUser: (rec: Rec | null) => void;
  /** The latest list-cards message (null when none) — drives UXView. */
  latestCards: CardsMessage | null;
  /** The latest draft-card message (null when none) — drives DraftPanel. */
  latestDraft: DraftCardMessage | null;
  /** The latest note-capture card (null when none) — drives NotePanel. */
  latestNote: NoteCardMessage | null;
  /** The latest Workflow surface (null when none) — drives WorkflowPanel. */
  latestWorkflow: WorkflowCardMessage | null;
  /** The confirmation card to show: an undecided one always (nothing may bury it), else the most
   *  recent decided one until it is dismissed or a newer surface replaces it — so its result
   *  (running / done / partial / failed / cancelled) is actually seen. Drives ConfirmCard. */
  latestConfirm: ConfirmMessage | null;
  /** Hide a decided confirmation card (its Close button); the previous surface comes back. */
  dismissConfirm: (id: string) => void;
  /** A confirmation the card rulebook wants as a blocking OVERLAY (dialog) — kept out of the
   *  workspace surfaces so the panel keeps showing what was there. Null when none. */
  overlayConfirm: ConfirmMessage | null;
  /** Whichever of cards/draft/note/workflow/confirm is the most-recent workspace surface (what to render). */
  latestSurface: CardsMessage | DraftCardMessage | NoteCardMessage | WorkflowCardMessage | ConfirmMessage | null;
  /** Push the on-screen draft's current fields to SAM (both agents); null clears it (sent/discarded). */
  emitDraftContext: (snapshot: DraftSnapshot | null) => void;
  /** Push the on-screen note to SAM (both agents) as active_note; null clears it. */
  emitNoteContext: (note: Rec | null) => void;
  /** Generic "report this surface's visible state to SAM" emit — the shared implementation behind
   *  useSamSurfaceContext (see that hook). `key` becomes the `active_<key>` field
   *  api-service/app/services/llm/ui_context.py parses. Prefer calling useSamSurfaceContext
   *  directly from the surface's own component instead of threading this down as a prop —
   *  it's exposed here only so ChatPage can feed it into SamSurfaceContext.Provider. */
  emitSurfaceContext: SamSurfaceEmit;
  /** Emit `opened` context for a user-initiated card open (not SAM-initiated). Looks up the title
   *  from the cached record list so the [SCREEN UPDATE] note names the item. Use for modules that
   *  don't have a dedicated active_* surface (e.g. properties, notifications). Pass null to clear. */
  openCardByUser: (module: SamModule, id: string | null) => void;
  reportUserList: (module: SamModule, filter: Rec, records: Rec[]) => void;
  /** Remove the note-capture card from the workspace. */
  closeNoteCard: () => void;
  /** Open / restart the note-capture card, optionally pre-linked to a context entity. */
  upsertNoteCard: (contextType?: string, entityId?: string) => void;
  /** Open the Workflow SETTINGS card directly (e.g. from "Customize" in CompanyWorkflowsListCard). */
  openWorkflowSettings: (messageId?: string) => void;
  /** Re-fetch the notes list and update its on-screen cards card (if one is showing) — call this
   *  after any client-side note write that bypasses SAM (delete/save/convert from NoteReviewCard)
   *  so a stale record (e.g. a just-discarded note) doesn't linger when the user navigates back
   *  to the notes list card. */
  refreshNotesList: () => void;
  refreshMyTodosList: () => void;
  /** The engagement_id SAM last asked to expand the Quick Quote section for. Compared against the
   *  currently-open engagement so WorkflowCard can auto-expand its inline Quick Quote section. */
  expandQuickQuoteEngagementId: string | null;
  /** Incremented on every REFRESH_QUICK_QUOTE so WorkflowCard can scroll to the section even when
   *  the expandQuickQuoteEngagementId hasn't changed (save → scroll without re-expanding). */
  quickQuoteScrollTick: number;
  /** The engagement_id SAM last asked to open a blocked transition's business_capture form for
   *  (EXPAND_ENGAGEMENT_TRANSITION_CAPTURE, from list_available_transitions). Compared against
   *  the currently-open engagement so WorkflowCard can auto-open the right transition's panel. */
  expandTransitionEngagementId: string | null;
  /** The transition_code whose business_capture panel to open — paired with
   *  expandTransitionEngagementId. */
  expandTransitionCode: string | null;
  /** The engagement_id SAM last asked to open the satellite map for. WorkflowCard uses this to
   *  auto-open the satellite map inside the Quick Quote section. */
  openQuickQuoteMapEngagementId: string | null;
  /** The engagement_id SAM last asked to open the Assessment capture card for
   *  (EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE, from request_assessment_media_capture — the user
   *  asked SAM to add a photo/voice note/report/note it can't accept itself). */
  expandAssessmentCaptureEngagementId: string | null;
  /** Bumped on every EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE so WorkflowCard re-triggers the
   *  expand+flash even when the engagement_id is unchanged from last time. */
  expandAssessmentCaptureTick: number;
  /** Last proposal_prepared_open tool call — open the named read-only panel on the Proposal
   *  Prepared card for the given engagement. `tick` increments so the same view can be re-opened. */
  proposalPreparedViewRequest: { engagementId: string; view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null;
  /** Last proposal_surface_interact tool call — an interaction INSIDE an already-open Proposal
   *  Prepared surface (option switch, section expand/collapse, item open, close, scroll). `tick`
   *  increments so the same action can be re-applied (e.g. "scroll down" twice in a row). */
  proposalSurfaceInteractRequest: ProposalSurfaceInteractPayload & { tick: number } | null;
}

// The record an action is bound to via `recordId` — deliberately NOT "the list happened to
// return exactly one row," since a filter's row count is transient (the same "Overdue Tasks"
// view might show one row today and five tomorrow) and shouldn't flip the "⋯" menu label between
// a generic name and a specific one. `recordId` is different: it means SAM's find-then-patch flow
// (search for a record, then update it) is genuinely about that one record, even when it never
// bothers to caption the search with a specific `title`. Engagements are named by their property
// address here, not their auto-generated title, matching how the rest of the app refers to them.
function singleRowTitle(module: SamModule, rows: Rec[], recordId?: string | null): string | undefined {
  if (!recordId) return undefined;
  const r = rows.find(row => cardId(module, row) === recordId);
  if (!r) return undefined;
  if (module === 'engagements') return sv(r.property_name) || cardInfo(module, r).title;
  return cardInfo(module, r).title;
}

/** The numbered rows the agent sees for a module's card list — ONE builder shared by the socket
 *  context_update, the canonical card_context and the WorkspaceView memo, so ordinal references
 *  ("the 3rd one") can never resolve against two different numberings.
 *  'properties' renders one card per PROPERTY (not per engagement); its status prefers the
 *  primary engagement's status, falling back to the property's own — same as WorkspacePropertyCard. */
function buildWorkspaceItems(module: SamModule, recs: Rec[]) {
  return module === 'properties'
    ? recs.map((r, i) => {
        const info = cardInfo('properties', r);
        const primary = ((r.engagements as Rec[]) || [])[0];
        const status = primary ? sv(primary.current_status || primary.stage) : info.status;
        return {
          index: i + 1, id: cardId('properties', r), title: info.title,
          status: status || undefined, subtitle: info.sub || undefined, priority: info.priority || undefined,
        };
      })
    : recs.map((r, i) => {
        const info = cardInfo(module, r);
        return {
          index: i + 1, id: cardId(module, r), title: info.title,
          status: info.status || undefined, subtitle: info.sub || undefined, priority: info.priority || undefined,
        };
      });
}

/** The card rulebook asked for this confirmation as a blocking overlay (cards/policy.py
 *  sensitive-action-confirmation → SHOW_TEMPORARY_UI {presentation: 'OVERLAY'}). */
const isOverlayConfirm = (m: ConfirmMessage) => m.presentation?.interaction?.presentation === 'OVERLAY';

export function useChatWorkspace({
  messages, setMessages, conversationId, mode, isDesktop, isPlatform, inlineActionCards,
  threadRef, setChatOn, setHfIndex, hfIndex, onPickEngagement, onOpenNoteTaker, onExecuteNoteFollowups, onSetNoteFollowupIncluded,
  onExpandNoteFollowup, openNotificationsPanel, recordView, onViewReplayed,
}: UseChatWorkspaceOptions): ChatWorkspace {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Card-catalogue-derived tools per module. Read through a ref inside callbacks so the many
  // memoised emitters don't re-create when the client config lands.
  const moduleTools = useModuleTools();
  const moduleToolsRef = useRef(moduleTools);
  moduleToolsRef.current = moduleTools;
  const cardRecordsRef = useRef<Partial<Record<SamModule, Rec[]>>>({});
  const lastFilterRef = useRef<Partial<Record<SamModule, Rec>>>({});
  // cards_version 2 — the card catalogue + the latest `card.open` event per module (from the
  // agent:action envelope's card_events), used to stamp card instances and build card_context.
  const cardCatalogue = useCardCatalogue();
  const cardEventRouting = useCardEventRouting();
  const cardCatalogueRef = useRef(cardCatalogue);
  cardCatalogueRef.current = cardCatalogue;
  const cardContextFields = useCardContextFields();
  const cardContextFieldsRef = useRef(cardContextFields);
  cardContextFieldsRef.current = cardContextFields;
  // Keyed by card_definition_id (engagement.list and engagement.view share one module).
  const cardEventsRef = useRef<Partial<Record<string, SamCardEvent>>>({});
  const onCardEvents = useCallback((events: SamCardEvent[]) => {
    for (const ev of events) {
      if (ev.type === 'card.open') cardEventsRef.current = { ...cardEventsRef.current, [ev.card_definition_id]: ev };
    }
  }, []);
  /** The server instance id of the list/panel card `module` shows with `filter`, if SAM opened it. */
  const listInstanceId = useCallback((module: SamModule, filter: Rec | undefined): string | null => {
    const def = resolveCardDefinition(cardCatalogueRef.current, module, null);
    if (!def) return null;
    return matchingCardEvent(cardEventsRef.current[def.id], def.id, filter as Record<string, unknown>)?.card_instance_id ?? null;
  }, []);
  /** `{ card_context }` for an agent.context_update about `module` — `{}` until the catalogue
   *  loads or when the module has no catalogue card (the backend then relies on the legacy keys). */
  const cardContextPatch = useCallback((module: SamModule, openedId: string | null, filter?: Rec, title?: string) => {
    const definition = resolveCardDefinition(cardCatalogueRef.current, module, openedId);
    if (!definition) return {};
    const card_context: CardContext = buildCardContext({
      definition,
      event: cardEventsRef.current[definition.id],
      items: buildWorkspaceItems(module, cardRecordsRef.current[module] || []),
      records: cardRecordsRef.current[module] || [],
      fieldPaths: cardContextFieldsRef.current[definition.id],
      openedId,
      filter: (filter ?? lastFilterRef.current[module]) as Record<string, unknown> | undefined,
      title,
    });
    return { card_context };
  }, []);
  /** Persist which row is open on the module's current list-card instance (sessions.card_instances). */
  const emitPresentation = useCallback((module: SamModule, openedId: string | null) => {
    const instanceId = listInstanceId(module, lastFilterRef.current[module]);
    if (!instanceId || !conversationId) return;
    socketService.emit('message', {
      type: 'card.presentation_update', conversation_id: conversationId,
      card_instance_id: instanceId, presentation_state: { selected_entity_id: openedId },
    });
  }, [conversationId, listInstanceId]);
  /** The user re-filtered a list card by hand: sync SAM to the rows now on screen. The filter no
   *  longer matches SAM's card.open event, so card_context carries `card_instance_id: null`. */
  const reportUserList = useCallback((module: SamModule, filter: Rec, records: Rec[]) => {
    if (!conversationId) return;
    // Paging isn't part of what the list means — keep it out of semantic_state.
    const semantic: Rec = { ...filter };
    delete semantic.skip; delete semantic.limit;
    lastFilterRef.current = { ...lastFilterRef.current, [module]: semantic };
    cardRecordsRef.current = { ...cardRecordsRef.current, [module]: records };
    socketService.emit('message', {
      type: 'agent.context_update', conversation_id: conversationId,
      context: {
        listed_module: module, items: buildWorkspaceItems(module, records), opened: null,
        available_actions: moduleToolsRef.current[module] ?? [],
        ...cardContextPatch(module, null, semantic),
      },
    });
  }, [conversationId, cardContextPatch]);
  // Latest-wins token for surface switches. A list open fetches its rows BEFORE bringing its card
  // to the front, so when two switches overlap (SAM's open + a user click, or two quick requests)
  // the slower fetch would land last and yank the screen back — a visible flash. Every switch
  // bumps this; an async one renders only if it's still the newest when its fetch resolves.
  const surfaceSeqRef = useRef(0);
  const [openCards, setOpenCards] = useState<Partial<Record<SamModule, string | null>>>({});
  // True when the property detail was opened by a SAM action (not a user list-click).
  // onOpenCard is only called from useSamActions (user clicks go directly to setOpenCards),
  // so setting this flag there reliably distinguishes the two paths.
  const [openCardSources, setOpenCardSources] = useState<Partial<Record<SamModule, 'list' | 'agent' | 'recent'>>>({});
  const setOpenCardSource = useCallback((module: SamModule, source: 'list' | 'agent' | 'recent') => {
    setOpenCardSources(prev => ({ ...prev, [module]: source }));
  }, []);
  const [expandQuickQuoteEngagementId, setExpandQuickQuoteEngagementId] = useState<string | null>(null);
  const [quickQuoteScrollTick, setQuickQuoteScrollTick] = useState(0);
  const [expandTransitionEngagementId, setExpandTransitionEngagementId] = useState<string | null>(null);
  const [expandTransitionCode, setExpandTransitionCode] = useState<string | null>(null);
  const [openQuickQuoteMapEngagementId, setOpenQuickQuoteMapEngagementId] = useState<string | null>(null);
  const [expandAssessmentCaptureEngagementId, setExpandAssessmentCaptureEngagementId] = useState<string | null>(null);
  const [expandAssessmentCaptureTick, setExpandAssessmentCaptureTick] = useState(0);
  // SAM's proposal_prepared_open tool — tracks the last requested view + engagement so WorkflowCard
  // can open the right panel. Stored as an object so changing from one view to the same view still
  // fires a new reference (needed to re-trigger the useEffect in WorkflowCard).
  const [proposalPreparedViewRequest, setProposalPreparedViewRequest] = useState<{ engagementId: string; view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null>(null);
  // SAM's proposal_surface_interact tool — an interaction inside an already-open surface.
  const [proposalSurfaceInteractRequest, setProposalSurfaceInteractRequest] =
    useState<ProposalSurfaceInteractPayload & { tick: number } | null>(null);

  // Thread auto-scroll (stick-to-bottom on new messages, instant on load) is owned by ChatThread,
  // which also handles scroll-up pagination and viewport preservation when older pages prepend.

  const fetchModuleRows = useCallback(async (module: SamModule, filter: Rec): Promise<Rec[]> => {
    const path = isPlatform
      ? ({ users: 'platform/users', roles: 'roles/all' } as Partial<Record<SamModule, string>>)[module]
      : undefined;
    try {
      const rows = await dispatch(
        workspaceApi.endpoints.getModuleRecords.initiate({ module, filter, path }, { forceRefetch: true }),
      ).unwrap();
      const personal = ((rows as Rec[]) || []).map(r => ({ ...r, source: 'personal' }));

      // My To-Dos also surfaces the caller's role-queue tasks (claimable, unassigned) as a
      // second section — same data the dedicated My To-Dos page shows under "Role Queue".
      if (module === 'my_todos') {
        try {
          const queueRows = await dispatch(
            workspaceApi.endpoints.getModuleRecords.initiate(
              { module, filter, path: 'tasks/my-role-queue' }, { forceRefetch: true },
            ),
          ).unwrap();
          const queue = ((queueRows as Rec[]) || []).map(r => ({
            ...r, item_id: r['task_id'], item_type: 'task', source: 'queue',
          }));
          return [...personal, ...queue];
        } catch {
          return personal;
        }
      }
      return personal;
    } catch {
      return [];
    }
  }, [dispatch, isPlatform]);

  const renderModuleCards = useCallback((module: SamModule, recs: Rec[], filter: Rec, facets?: SamFacet[], title?: string, caption?: string, messageId?: string) => {
    cardRecordsRef.current = { ...cardRecordsRef.current, [module]: recs };
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'cards' && (m as CardsMessage).module === module);
      const prevCard = idx !== -1 ? (prev[idx] as CardsMessage) : undefined;
      const msg: CardsMessage = {
        kind: 'cards', id: idx !== -1 ? prev[idx].id : `cd-${Date.now()}-${module}`,
        module, records: recs, filter, facets,
        // Keep the prior view's title/caption/turn-id when a refresh/replay omits them.
        title: title ?? prevCard?.title,
        caption: caption ?? prevCard?.caption,
        messageId: messageId ?? prevCard?.messageId,
        cardDefinitionId: resolveCardDefinition(cardCatalogueRef.current, module, null)?.id ?? prevCard?.cardDefinitionId,
        cardInstanceId: listInstanceId(module, filter),
      };
      // Re-showing a module must bring its card to the FRONT (the workspace renders `latestCards`
      // = the last cards message). Updating in place would leave a more-recently-shown module last,
      // so the panel wouldn't switch back (e.g. users → roles → users kept showing roles). Drop the
      // old position and append. Cards aren't in the chat thread, so reordering is display-only.
      const withoutOld = idx !== -1 ? [...prev.slice(0, idx), ...prev.slice(idx + 1)] : prev;
      // An undecided confirmation card stays on top regardless of order — see pendingConfirm below.
      return [...withoutOld, msg];
    });
    setHfIndex(0);
    // A new users list invalidates any previously focused user; the hands-free effect / a card
    // open will set the new target. Prevents "rename this" hitting a stale selection.
    if (module === 'users') dispatch(setSelectedUser(null));
    if (!isDesktop) setChatOn(false);
    // The 'properties' panel renders ONE CARD PER PROPERTY (WorkspacePropertyCard), each showing
    // its own nested engagements — it does NOT flatten every engagement into one continuous list.
    // This used to flatten by engagement (matching an old, incorrect list_properties docstring
    // claim), which meant a property with 0 engagements contributed 0 positions and became
    // completely unaddressable by ordinal — "open the last one" right after creating a
    // zero-engagement property silently resolved to an unrelated, older engagement instead.
    // `index` now matches the property's own on-screen card position (see search_engagement's own
    // `position` field, added for the same reason); each entry's status/title prefer the
    // property's primary engagement when it has one, falling back to the property's own
    // active/inactive/sold status otherwise — same fallback WorkspacePropertyCard itself uses.
    // A reference to a SPECIFIC engagement on a multi-engagement property ("the second job")
    // isn't separately numbered here — resolve that from the engagement-level `position` field
    // in the most recent search_engagement tool result instead.
    // `subtitle`/`priority` reuse cardInfo's own per-module computation (address+owner for
    // properties, due date for tasks, etc.) so the agent can tell two same-titled items apart.
    const items = buildWorkspaceItems(module, recs);
    const availableActions = moduleToolsRef.current[module] ?? [];
    socketService.emit('message', {
      type: 'agent.context_update', conversation_id: conversationId,
      context: {
        listed_module: module, items, opened: null, available_actions: availableActions,
        ...cardContextPatch(module, null, filter, title),
      },
    });
    if (mode === 'interactive') {
      socketService.emit('message', {
        type: 'agent_realtime.session.update', session_id: conversationId,
        // `items` (with real ids) alongside `items_summary` — receive_context_update's live
        // [SCREEN UPDATE] note reads `items` to name each row's id; without it the note only ever
        // said "the tasks list on screen changed" and the live session had no id to resolve a
        // follow-up like "the 3rd one" against until its next full instructions rebuild.
        context_patch: { listed_module: module, items, items_summary: items.map(it => `${it.index}. ${it.title}`).join('; ') },
      });
    }
  }, [conversationId, mode, isDesktop, setMessages, setChatOn, setHfIndex, dispatch, cardContextPatch, listInstanceId]);

  // Mark the user the agent should treat as the rename target (focused card / hands-free big card).
  // Persists to the UI-context store (so it rides along in the next message `meta`) AND pushes a
  // conversation-scoped update so a live voice session learns the change immediately. `null` clears.
  const selectUser = useCallback((rec: Rec | null) => {
    const su = rec
      ? {
          id: cardId('users', rec),
          name: cardInfo('users', rec).title,
          role: Array.isArray(rec.role_names) ? (rec.role_names as string[])[0] : undefined,
          status: cardInfo('users', rec).status || undefined,
        }
      : null;
    dispatch(setSelectedUser(su));
    const patch = { listed_module: 'users', selected_user: su };
    socketService.emit('message', { type: 'agent.context_update', conversation_id: conversationId, context: patch });
    if (mode === 'interactive') {
      socketService.emit('message', { type: 'agent_realtime.session.update', session_id: conversationId, context_patch: patch });
    }
  }, [conversationId, mode, dispatch]);

  // Upsert the single draft-card surface. Same communication → reuse the card and bump `rev` so
  // ComposeCard re-seeds from the fresh payload (SAM live edit); a different/absent communication →
  // a new card (ComposeCard, keyed by communication_id, remounts and loads that record). Brings the
  // draft to the front so it becomes the latest workspace surface.
  const upsertDraftCard = useCallback((communicationId: string | undefined, seed: ComposeDraftInput | undefined, messageId?: string) => {
    surfaceSeqRef.current++;
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'draftcard');
      const prevMsg = idx !== -1 ? (prev[idx] as DraftCardMessage) : undefined;
      const sameRecord = !!prevMsg && !!communicationId && prevMsg.communication_id === communicationId;
      const msg: DraftCardMessage = sameRecord
        ? { ...prevMsg!, draft: seed ?? prevMsg!.draft, rev: (prevMsg!.rev ?? 0) + 1, messageId: messageId ?? prevMsg!.messageId }
        : { kind: 'draftcard', id: `dc-${Date.now()}`, communication_id: communicationId, draft: seed, rev: 1, messageId };
      if (idx !== -1) return [...prev.slice(0, idx), ...prev.slice(idx + 1), msg];
      return [...prev, msg];
    });
    setHfIndex(0);
    if (!isDesktop) setChatOn(false);
  }, [setMessages, setHfIndex, isDesktop, setChatOn]);

  // Open (or restart) the single note-capture card in the workspace, bringing it to the front so it
  // becomes the latest surface. `startRev` is bumped each open so NotePanel (re)starts recording.
  const upsertNoteCard = useCallback((contextType?: string, entityId?: string) => {
    surfaceSeqRef.current++;
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'notecard');
      const prevMsg = idx !== -1 ? (prev[idx] as NoteCardMessage) : undefined;
      const msg: NoteCardMessage = {
        kind: 'notecard', id: prevMsg?.id ?? `nc-${Date.now()}`,
        contextType, entityId,
        startRev: (prevMsg?.startRev ?? 0) + 1,
        stopRev: prevMsg?.stopRev,
      };
      if (idx !== -1) return [...prev.slice(0, idx), ...prev.slice(idx + 1), msg];
      return [...prev, msg];
    });
    setHfIndex(0);
    if (!isDesktop) setChatOn(false);
  }, [setMessages, setHfIndex, isDesktop, setChatOn]);

  // SAM stop_note_taker — bump stopRev so the on-screen NotePanel stops the active recording.
  const requestStopNote = useCallback(() => {
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'notecard');
      if (idx === -1) return prev;
      const prevMsg = prev[idx] as NoteCardMessage;
      const msg: NoteCardMessage = { ...prevMsg, stopRev: (prevMsg.stopRev ?? 0) + 1 };
      return [...prev.slice(0, idx), ...prev.slice(idx + 1), msg];
    });
  }, [setMessages]);

  const closeNoteCard = useCallback(() => {
    setMessages(prev => prev.filter(m => m.kind !== 'notecard'));
  }, [setMessages]);

  const dismissConfirm = useCallback((id: string) => {
    setMessages(prev => prev.map(m => (m.id === id && m.kind === 'confirm' ? { ...m, dismissed: true } as ConfirmMessage : m)));
  }, [setMessages]);

  // Upsert the approvals-only workflow surface (no engagementId). Engagement-specific workflow
  // views are not opened as standalone surfaces — engagements are always accessed via the
  // property → engagements path in UXView. Only list_pending_approvals (no engagementId) uses this.
  const upsertWorkflowCard = useCallback((engagementId: string | undefined, messageId?: string) => {
    // Suppress the standalone surface when an engagement is specified — it's accessible via the
    // property/engagements workspace path and should not open as a separate panel.
    if (engagementId) return;
    surfaceSeqRef.current++;
    setMessages(prev => {
      // Only the approvals/engagement surface — the Company Processes card (`mode: 'list'`) is
      // also a non-settings workflowcard, and matching it re-showed that list (mode and all)
      // whenever Pending Approvals was opened after it.
      const idx = prev.findIndex(m => m.kind === 'workflowcard' && ((m as WorkflowCardMessage).mode ?? 'engagement') === 'engagement');
      const prevMsg = idx !== -1 ? (prev[idx] as WorkflowCardMessage) : undefined;
      const sameRecord = !!prevMsg && prevMsg.engagementId === engagementId;
      const msg: WorkflowCardMessage = sameRecord
        ? { ...prevMsg!, rev: (prevMsg!.rev ?? 0) + 1, messageId: messageId ?? prevMsg!.messageId }
        : { kind: 'workflowcard', id: `wf-${Date.now()}`, engagementId, rev: 1, messageId };
      if (idx !== -1) return [...prev.slice(0, idx), ...prev.slice(idx + 1), msg];
      return [...prev, msg];
    });
    setHfIndex(0);
    if (!isDesktop) setChatOn(false);
  }, [setMessages, setHfIndex, isDesktop, setChatOn]);

  // REFRESH_WORKFLOW — execute_workflow_transition / decide_workflow_approval hit the workflow
  // service directly (bypassing the frontend's own RTK mutations), so nothing else invalidates the
  // open surface's cache. Re-fetching by tag covers state + transitions (keyed by engagementId) and
  // the approvals list (untagged by engagement).
  const refreshWorkflowCard = useCallback((engagementId: string | undefined) => {
    dispatch(workflowEngineApi.util.invalidateTags([
      ...(engagementId ? [
        { type: 'EngagementWorkflowState' as const, id: engagementId },
        { type: 'AvailableTransitions' as const, id: engagementId },
      ] : []),
      'PendingApprovals' as const,
    ]));
  }, [dispatch]);

  // Upsert the single Workflow SETTINGS surface (tenant's whole workflow STRUCTURE — no engagement
  // in the picture). A SECOND, DISTINCT `workflowcard` surface from the one above — always reuses
  // the existing settings card (there's only ever one) and bumps `rev` so WorkflowSettingsCard
  // refetches (SAM re-opened/refreshed it or a mutation tool changed the structure elsewhere).
  const upsertWorkflowSettingsCard = useCallback((messageId?: string) => {
    surfaceSeqRef.current++;
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'workflowcard' && (m as WorkflowCardMessage).mode === 'settings');
      const prevMsg = idx !== -1 ? (prev[idx] as WorkflowCardMessage) : undefined;
      const msg: WorkflowCardMessage = prevMsg
        ? { ...prevMsg, rev: (prevMsg.rev ?? 0) + 1, messageId: messageId ?? prevMsg.messageId }
        : { kind: 'workflowcard', id: `wfs-${Date.now()}`, mode: 'settings', rev: 1, messageId };
      if (idx !== -1) return [...prev.slice(0, idx), ...prev.slice(idx + 1), msg];
      return [...prev, msg];
    });
    setHfIndex(0);
    if (!isDesktop) setChatOn(false);
  }, [setMessages, setHfIndex, isDesktop, setChatOn]);

  // Upsert the Company Processes list card (mode: 'list') — the overview step before the user
  // clicks "Customize" to open the full WorkflowSettingsCard. Always reuses the existing list
  // card (there's only ever one) and bumps `rev` so it refetches on re-open.
  const upsertWorkflowListCard = useCallback((messageId?: string) => {
    surfaceSeqRef.current++;
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'workflowcard' && (m as WorkflowCardMessage).mode === 'list');
      const prevMsg = idx !== -1 ? (prev[idx] as WorkflowCardMessage) : undefined;
      const msg: WorkflowCardMessage = prevMsg
        ? { ...prevMsg, rev: (prevMsg.rev ?? 0) + 1, messageId: messageId ?? prevMsg.messageId }
        : { kind: 'workflowcard', id: `wfl-${Date.now()}`, mode: 'list', rev: 1, messageId };
      if (idx !== -1) return [...prev.slice(0, idx), ...prev.slice(idx + 1), msg];
      return [...prev, msg];
    });
    setHfIndex(0);
    if (!isDesktop) setChatOn(false);
  }, [setMessages, setHfIndex, isDesktop, setChatOn]);

  // REFRESH_WORKFLOW_SETTINGS — toggle_workflow_item / rename_workflow_label / add_workflow_* /
  // set_transition_authority hit the customization service directly (bypassing the frontend's own
  // RTK mutations), so nothing else invalidates the open settings surface's cache.
  const refreshWorkflowSettingsCard = useCallback(() => {
    dispatch(workflowEngineApi.util.invalidateTags(['WorkflowCustomization' as const]));
  }, [dispatch]);

  // The four surfaces below used to each carry their own near-identical "push into the ⋯ menu"
  // helper, differing only in which field they deduped on (communicationId / engagementId /
  // workflowSettings / workflowList). They are now one call into `recordView`, which dedupes on
  // `contextRef` — the single field that replaced all four flags.

  // A communication draft: re-openable from the menu by its communication id.
  const pushDraftHistory = useCallback((communicationId: string | undefined, title?: string, caption?: string) => {
    if (!communicationId) return;
    recordView({
      cardType: 'communications', cardDefinitionId: 'communication.compose', title, subtitle: caption,
      contextRef: { type: CONTEXT_REF_TYPE.COMMUNICATION, id: communicationId },
    });
  }, [recordView]);

  // The per-engagement Workflow card — or, with no engagement, the approvals-only view.
  const pushWorkflowHistory = useCallback((engagementId: string | undefined, title?: string, caption?: string) => {
    recordView({
      cardType: 'workflow', cardDefinitionId: 'engagement.view',
      title: title || (engagementId ? 'Workflow' : 'Pending Approvals'), subtitle: caption,
      contextRef: engagementId ? { type: CONTEXT_REF_TYPE.ENGAGEMENT, id: engagementId } : undefined,
    });
  }, [recordView]);

  // The tenant-wide workflow STRUCTURE editor — a single surface, hence a fixed context ref.
  const pushWorkflowSettingsHistory = useCallback((title?: string, caption?: string) => {
    recordView({
      cardType: 'workflow', cardDefinitionId: 'workflow_settings.view',
      title: title || 'Workflow Settings', subtitle: caption,
      contextRef: { type: CONTEXT_REF_TYPE.WORKFLOW_SETTINGS, id: '' },
    });
  }, [recordView]);

  // The Company Processes overview list.
  const pushWorkflowListHistory = useCallback((title?: string, caption?: string) => {
    recordView({
      cardType: 'workflow', cardDefinitionId: 'company_workflow.list',
      title: title || 'Company Processes', subtitle: caption,
      contextRef: { type: CONTEXT_REF_TYPE.COMPANY_WORKFLOWS, id: '' },
    });
  }, [recordView]);

  // Generic "report this surface's visible state to SAM" emit, shared by every active_* surface —
  // the single implementation behind useSamSurfaceContext (see that file) and, during migration,
  // behind the individual emit*Context wrappers below. `key` becomes the `active_<key>` field
  // api-service/app/services/llm/ui_context.py parses.
  //
  // Also replays every surface's last-known state into a freshly-started interactive session —
  // generalizes what emitWorkflowContext used to do only for itself (lastWorkflowPatchRef): a
  // surface can be opened while in text mode, then the user starts voice, and without this the
  // realtime session never learns what's already on screen until something changes it again.
  const lastSurfaceStateRef = useRef<Record<string, unknown>>({});
  // Tracks which hook instance (by useId) last wrote each surface key. A null clear is rejected
  // unless it comes from that owner, preventing collapsed-list cards from wiping the active card's
  // context on mount, reorder, unmount, or list refresh.
  const lastSurfaceOwnerRef = useRef<Record<string, string>>({});
  const emitSurfaceContext = useCallback((key: string, state: unknown, ownerId: string): boolean => {
    // Ownership and state are recorded before the conversationId guard so that:
    // (a) the hook can rely on lastSurfaceStateRef for the interactive-mode replay even when
    //     conversationId arrives after the initial emit, and
    // (b) the ownership model stays consistent regardless of connection timing.
    if (state === null) {
      if (lastSurfaceOwnerRef.current[key] !== ownerId) return true; // rejected — already "handled"
      delete lastSurfaceOwnerRef.current[key];
    } else {
      lastSurfaceOwnerRef.current[key] = ownerId;
    }
    lastSurfaceStateRef.current[key] = state;
    if (!conversationId) return false; // recorded for replay; socket emit deferred
    const patch = { [`active_${key}`]: state };
    socketService.emit('message', { type: 'agent.context_update', conversation_id: conversationId, context: patch });
    if (mode === 'interactive') {
      socketService.emit('message', { type: 'agent_realtime.session.update', session_id: conversationId, context_patch: patch });
    }
    return true;
  }, [conversationId, mode]);
  useEffect(() => {
    if (mode !== 'interactive' || !conversationId) return;
    for (const [key, state] of Object.entries(lastSurfaceStateRef.current)) {
      socketService.emit('message', {
        type: 'agent_realtime.session.update', session_id: conversationId,
        context_patch: { [`active_${key}`]: state },
      });
    }
  }, [mode, conversationId]);

  // Push the draft currently on screen (its live field values) to SAM. Rides the same
  // context_update / session.update channels as the card-list context, so BOTH the text agent
  // (merged ui_context per turn) and the voice agent (receive_context_update) learn what the user
  // is editing. `null` clears it once the draft is sent/discarded/closed.
  const emitDraftContext = useCallback((snapshot: DraftSnapshot | null) => {
    emitSurfaceContext('draft', snapshot, 'internal');
  }, [emitSurfaceContext]);

  // Push the note the user is reviewing (its current fields) to SAM — the note equivalent of
  // emitDraftContext. Lets both agents resolve "this note" / "edit the transcript" / "turn the
  // first follow-up into a task" to the on-screen note (via delegate). `null` clears it.
  const noteRealtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emitNoteContext = useCallback((note: Rec | null) => {
    if (!conversationId) return;
    const patch = note
      ? {
          active_note: {
            note_id: cardId('notes', note),
            title: sv(note.title) || sv(note.agent_title),
            status: sv(note.status),
            summary: sv(note.summary || note.review_summary),
            categories: Array.isArray(note.custom_tags) ? (note.custom_tags as string[]) : [],
            follow_ups: (Array.isArray(note.suggestions) ? (note.suggestions as Rec[]) : [])
              .filter((s) => !s.dismissed)
              .map((s) => ({ suggestion_id: sv(s.suggestion_id), label: sv(s.label) })),
            // Full-ish transcript so the supervisor can edit/append against the CURRENT value.
            transcript_preview: sv(note.clean_transcript || note.final_transcript).slice(0, 4000),
            // Current business-record link (if any), so the agent knows what's already linked
            // instead of only learning it from its own tool call result.
            context: note.context && typeof note.context === 'object'
              ? {
                  context_type: sv((note.context as Rec).context_type),
                  entity_id: sv((note.context as Rec).entity_id),
                  display_label: sv((note.context as Rec).display_label),
                }
              : null,
          },
        }
      : { active_note: null };
    // Text agent: update immediately (no voice response triggered).
    socketService.emit('message', { type: 'agent.context_update', conversation_id: conversationId, context: patch });
    // Realtime agent: debounce to collapse rapid-fire backend analysis updates (multiple
    // onNoteSynced calls) into one inject — each inject triggers a separate voice response.
    if (mode === 'interactive') {
      if (noteRealtimeDebounceRef.current) clearTimeout(noteRealtimeDebounceRef.current);
      noteRealtimeDebounceRef.current = setTimeout(() => {
        socketService.emit('message', { type: 'agent_realtime.session.update', session_id: conversationId, context_patch: patch });
      }, 1500);
    }
  }, [conversationId, mode]);

  // A reload mid-recording never runs NotePanel's cleanup, so the server-side ui_context (1h TTL)
  // would keep reporting a live "capture:" note and start_note_taker would refuse to start. No
  // capture survives a fresh workspace mount, so clear it once per conversation.
  useEffect(() => {
    if (!conversationId) return;
    socketService.emit('message', { type: 'agent.context_update', conversation_id: conversationId, context: { active_note: null } });
  }, [conversationId]);

  // active_task, active_meeting, active_engagement_workflow, active_proposal_surface and
  // active_quick_quote are now reported directly by their leaf components via
  // useSamSurfaceContext (see that hook) instead of a bespoke emit*Context function threaded down
  // as a prop through every intermediate component — see MEMORY/session notes on why: two of
  // those five chains shipped broken (Quick Quote never reported at all; one of
  // ProposalDisplayCard's two render branches silently lacked the report effect its sibling had),
  // and a third (task/meeting) was wired to components that turned out to be dead code, never
  // actually rendered. emitSurfaceContext above (used by useSamSurfaceContext via
  // SamSurfaceContext.Provider, see ChatPage.tsx) is the shared implementation all of them use now.

  // Emit `opened` context for a user-initiated card open (the counterpart to onOpenCard which
  // only fires for SAM-initiated opens). Looks up the record title from the cached list so the
  // [SCREEN UPDATE] note can name the item. Null clears it (user navigated back to the list).
  // Call this for modules that lack a dedicated active_* surface (properties, notifications) —
  // tasks/meetings/notes emit active_task/active_meeting/active_note via their own detail panels.
  const openCardByUser = useCallback((module: SamModule, id: string | null) => {
    if (!conversationId) return;
    const rec = id ? (cardRecordsRef.current[module] || []).find(r => cardId(module, r) === id) : undefined;
    // Falls back to a module-derived label (not blank) when the record isn't in the local cache
    // yet — e.g. a notification-driven open that never went through this module's list fetch.
    const title = rec ? cardInfo(module, rec).title : viewLabel(module, {});
    // Engagements read as their property in the "⋯" menu ("456 Birchwood Ln") rather than their
    // own auto-generated title — matching the list-search fallback in `pushHistory` below.
    const menuTitle = rec && module === 'engagements' ? (sv(rec.property_name) || title) : title;

    // Record this detail as its own "⋯" menu entry, distinct from whatever list view it was
    // opened from — so listing properties then clicking into one persists BOTH: the list (already
    // recorded when it was opened) and this specific property's detail. Every module funnels
    // through this one function on a manual open (see UXView's onUserOpenCard call sites), so this
    // covers tasks/meetings/notes/properties/engagements uniformly. `id === null` is the user
    // going BACK to the list, not a new view — nothing to record. 'notifications' is an overlay
    // with no persistent panel to replay into, so it's excluded like the active_* reset above.
    if (id && module !== 'notifications') {
      recordView({ cardType: module, title: menuTitle, contextRef: contextRefFor(module, id), source: 'user_open' });
      // Mark this as a user-list-open so the detail shows a Back button.
      setOpenCardSource(module, 'list');
    } else if (!id) {
      // User navigated back to the list — clear the source so reopening via ⋯ starts clean.
      setOpenCardSources(prev => { const n = { ...prev }; delete n[module]; return n; });
    }

    // Reset all active_* surfaces first, then re-populate only the surface matching this module.
    // Without the reset, switching from tasks→properties leaves active_task stale in ui_context.
    // Exception: 'notifications' is an overlay — don't wipe surfaces open underneath it, but DO
    // clear the stale items list so the agent doesn't see old records alongside notifications.
    //
    // active_task and active_meeting are intentionally NOT repopulated here for tasks/meetings.
    // TaskCard and WorkspaceMeetingCard both call useSamSurfaceContext, which emits the full
    // payload on their next render — richer than a list-record snapshot and with no risk of a
    // partial overwrite sticking if the card is already expanded (its hook would dedup and miss
    // the correction). my_todos maps item_id→meeting_id so its bridge stays (the hook can't do
    // that remapping). active_note and active_approval_id have no hook equivalent.
    const activeCtx: Rec = module !== 'notifications'
      ? { active_task: null, active_meeting: null, active_note: null }
      : { items: [] };
    if (module === 'my_todos') {
      // my_todos records use item_id as their primary key (not meeting_id)
      activeCtx.active_meeting = rec
        ? { meeting_id: sv(rec.item_id), title: sv(rec.title), status: sv(rec.status), meeting_type: sv(rec.meeting_type), start_time: sv(rec.start_time) }
        : null;
    } else if (module === 'notes') {
      activeCtx.active_note = rec
        ? { note_id: sv(rec.note_id ?? rec.id), title: sv(rec.title ?? rec.agent_title), status: sv(rec.status) }
        : null;
    } else if (module === 'notifications' && id) {
      // id is an approval ID from the action_route (not a notification_id) — surface it directly
      activeCtx.active_approval_id = id;
    }

    socketService.emit('message', {
      type: 'agent.context_update', conversation_id: conversationId,
      context: {
        listed_module: module, opened: id ?? null, available_actions: moduleToolsRef.current[module] ?? [], ...activeCtx,
        ...cardContextPatch(module, id ?? null),
      },
    });
    emitPresentation(module, id ?? null);
    if (mode === 'interactive') {
      socketService.emit('message', {
        type: 'agent_realtime.session.update', session_id: conversationId,
        context_patch: { listed_module: module, opened: id ?? null, opened_title: menuTitle ?? undefined, ...activeCtx },
      });
    }
  }, [conversationId, mode, recordView, cardContextPatch, emitPresentation]);

  // active_workflow_settings is now reported directly by WorkflowSettingsCard via
  // useSamSurfaceContext — see the comment above openCardByUser.

  // Log a list/panel view the agent opened. Recording is deduped by (module, contextRef, filter),
  // so asking for the same list twice bumps one entry instead of stacking near-identical rows.
  const pushHistory = useCallback((module: SamModule, filter: Rec, title?: string, caption?: string, messageId?: string, recordId?: string | null, view?: { title?: string; caption?: string }, rows?: Rec[]) => {
    recordView({
      cardType: module,
      // Menu-only label first (a write naming its entry after the record it just touched), then —
      // when the action is bound to (or the list narrowed to) one specific record — that record's
      // own name, since that beats whatever generic module-level label the backend sent for a bare
      // search/list read. Falls back to the card's own title, then a filter-derived label.
      title: view?.title || (rows ? singleRowTitle(module, rows, recordId) : undefined) || title || viewLabel(module, filter),
      subtitle: view?.caption || caption,
      viewParams: filter,
      contextRef: contextRefFor(module, recordId),
      messageId,
    });
  }, [recordView]);

  // WorkspaceMeetingList falls back to (and re-syncs from) this shared filter for any field the
  // incoming view leaves unset, so every path that lands on a meetings view must overwrite it —
  // or the chips from the last view (a SAM filter, the user's own click) leak into the new one.
  const syncMeetingFilter = useCallback((f: Rec) => {
    dispatch(setMeetingFilter({
      status: f.status as string | undefined,
      meeting_type: f.meeting_type as string | undefined,
      search: f.search as string | undefined,
      organizer_user_id: f.organizer_user_id as string | undefined,
      updated_by_user_id: f.updated_by_user_id as string | undefined,
    }));
  }, [dispatch]);

  // Re-run a saved view: fetch its rows and bring that module's cards to the front (visible).
  // Which surface an entry re-opens is decided by its `contextRef.type` — the one field that
  // replaced the old communicationId / workflowList / workflowSettings / engagementId flags.
  const replayView = useCallback(async (rawEntry: RecentEntry) => {
    // An engagement-bound Workflow entry (SAM's OPEN_WORKFLOW_PANEL for an engagement) has no
    // standalone surface — upsertWorkflowCard deliberately no-ops with an engagementId — so the
    // click did nothing. Re-open it the way engagements are always shown: as an 'engagements' detail.
    const entry: RecentEntry = rawEntry.cardType === 'workflow' && rawEntry.contextRef?.type === CONTEXT_REF_TYPE.ENGAGEMENT && rawEntry.contextRef.id
      ? { ...rawEntry, cardType: 'engagements' as SamModule, viewParams: {} }
      : rawEntry;
    // Re-picking an entry makes it the most recent one in the menu (bumps last_accessed_at).
    recordView({
      cardType: rawEntry.cardType, cardDefinitionId: rawEntry.cardDefinitionId, title: rawEntry.title, subtitle: rawEntry.subtitle,
      contextRef: rawEntry.contextRef, viewParams: rawEntry.viewParams, messageId: rawEntry.messageId,
      source: 'replay',
    });
    const ctxType = entry.contextRef?.type;
    // A draft entry re-opens the draft card by id (ComposeCard loads the record — draft, sent, or
    // discarded); there are no list rows to fetch.
    if (ctxType === CONTEXT_REF_TYPE.COMMUNICATION) {
      upsertDraftCard(entry.contextRef!.id, undefined);
      onViewReplayed?.();
      return;
    }
    // The Company Processes list surface re-opens WorkflowPanel in `mode: 'list'` — no rows to fetch.
    if (ctxType === CONTEXT_REF_TYPE.COMPANY_WORKFLOWS) {
      upsertWorkflowListCard(undefined);
      onViewReplayed?.();
      return;
    }
    // The workflow SETTINGS surface re-opens WorkflowPanel in `mode: 'settings'` — no rows to fetch.
    if (ctxType === CONTEXT_REF_TYPE.WORKFLOW_SETTINGS) {
      upsertWorkflowSettingsCard(undefined);
      onViewReplayed?.();
      return;
    }
    // A Workflow surface entry re-opens the WorkflowPanel bound to its engagement (or the
    // approvals-only view when there's no engagement) — there are no list rows to fetch.
    if (entry.cardType === 'workflow') {
      upsertWorkflowCard(ctxType === CONTEXT_REF_TYPE.ENGAGEMENT ? entry.contextRef?.id : undefined, undefined);
      onViewReplayed?.();
      return;
    }
    // A Quick Quote entry re-opens the card straight from its stored `viewParams` — there is no
    // backend GET-by-id endpoint (no persisted record), so replay must reconstruct the whole
    const f = (entry.viewParams || {}) as Rec;
    lastFilterRef.current[entry.cardType] = f;
    // An entry bound to a record (a task/meeting/note/property/engagement opened from its list —
    // openCardByUser, or a property opened at a specific engagement) restores that detail too —
    // otherwise replaying it drops the user back on the bare list. `_RECORD_NOUN`/`RECORD_NOUN`
    // map BOTH 'workflow' and 'engagements' cardTypes to CONTEXT_REF_TYPE.ENGAGEMENT — 'workflow'
    // already returned above, so it never reaches here; an 'engagements' entry falls through to
    // this generic restore, which is correct: it's the same setOpenCards mechanism UXView's
    // onUserOpenCard('engagements', id) already uses to open EngagementAsPropertyDetail.
    // Set it BEFORE awaiting the list fetch below: the detail view it drives (TaskList/
    // WorkspaceMeetingList/etc.) fetches that one record by id itself and ignores the list
    // result, so there's no reason to make it wait on a full module-list round trip first.
    if (entry.contextRef?.id) {
      setOpenCards(prev => ({ ...prev, [entry.cardType]: entry.contextRef!.id }));
      // Replaying from ⋯ is a direct/recent navigation — back button must NOT appear.
      setOpenCardSource(entry.cardType, 'recent');
    } else {
      // Replaying a list entry (no specific record) — clear any open detail so the list shows.
      setOpenCards(prev => ({ ...prev, [entry.cardType]: null }));
      setOpenCardSources(prev => { const n = { ...prev }; delete n[entry.cardType]; return n; });
    }
    const surfaceSeq = ++surfaceSeqRef.current;
    const recs = await fetchModuleRows(entry.cardType, f);
    if (surfaceSeq !== surfaceSeqRef.current) return;
    // Dropping the old card makes renderModuleCards mint a fresh id, and UXView keys the list on
    // it — so the list re-seeds from this view's filter even when it equals the one already on
    // screen. Without the remount, chips/search the user changed since then survive the replay.
    setMessages(prev => prev.filter(m => !(m.kind === 'cards' && (m as CardsMessage).module === entry.cardType)));
    if (entry.cardType === 'meetings') syncMeetingFilter(f);
    renderModuleCards(entry.cardType, recs, f, undefined, entry.title, entry.subtitle);
    onViewReplayed?.();
    if (!isDesktop) setChatOn(false);
  }, [fetchModuleRows, renderModuleCards, recordView, isDesktop, setMessages, setChatOn, upsertDraftCard, upsertWorkflowCard, upsertWorkflowSettingsCard, upsertWorkflowListCard, syncMeetingFilter, onViewReplayed]);

  // Re-fetch a module's rows and, if its cards list is currently on screen, update it in place.
  // Used both for SAM's REFRESH_<module> actions and for client-only writes (e.g. discarding a
  // note from NoteReviewCard) that mutate a record's OWN api slice (notesApi) without ever
  // telling the generic workspace card cache (workspaceApi/cardRecordsRef) to refresh — those are
  // two separate caches, so a delete in one never invalidates the other on its own.
  const refreshModuleCard = useCallback(async (module: SamModule, messageId?: string) => {
    // Communications has no list card — SAM sent/discarded the draft on screen. Invalidate that
    // record so the ComposeCard reloads and flips from the draft editor to the sent/discarded view.
    // Derived straight from `messages` (a hook param, available from the top) rather than the
    // `latestDraft` memo declared further down — avoids a stale closure / declaration-order issue.
    if (module === 'communications') {
      const draftMsg = [...messages].reverse().find((m): m is DraftCardMessage => m.kind === 'draftcard');
      const id = draftMsg?.communication_id;
      if (id) dispatch(workspaceApi.util.invalidateTags([{ type: 'Communication' as never, id }]));
      return;
    }
    // Notes also live in a separate RTK api (notesApi). Invalidate its 'Note' cache so the
    // on-screen note-capture review card (NotePanel → useGetNoteQuery) and any note detail refetch
    // after a supervisor update_note/reanalyze/convert (which hit the backend directly), or a
    // client-side delete/save/convert in NoteReviewCard.
    if (module === 'notes') {
      dispatch(notesApi.util.invalidateTags(['Note' as never]));
    }
    // Meetings has its own standalone panel (MeetingList/WorkspaceMeetingList) backed by
    // useGetMeetingsListQuery, which reads from workspaceApi's 'Meeting' tag — not from the
    // ephemeral chat "cards" state this function updates below. Without this, a status update
    // from SAM (e.g. update_meeting_status) refreshes the in-chat card but leaves that panel stale.
    if (module === 'meetings') {
      dispatch(workspaceApi.util.invalidateTags(['Meeting' as never]));
    }
    // Refreshes are sent blindly (backend entity_changed) — only re-fetch a list that is actually
    // rendered; for anything else the tag invalidations above are the whole job.
    if (!messages.some(m => m.kind === 'cards' && (m as CardsMessage).module === module)) return;
    const f = (lastFilterRef.current[module] || {}) as Rec;
    const recs = await fetchModuleRows(module, f);
    cardRecordsRef.current = { ...cardRecordsRef.current, [module]: recs };
    setMessages(prev => {
      const idx = prev.findIndex(m => m.kind === 'cards' && (m as CardsMessage).module === module);
      if (idx === -1) return prev;
      const prevCard = prev[idx] as CardsMessage;
      // Re-point the card's reply strip (mobile UXView) to THIS write turn's response. Updated IN
      // PLACE: a refresh is a data sync, never a navigation — moving the card to the end would pull
      // an off-screen module in front of whatever the user is looking at. Navigation to the written
      // record is the backend's call (an explicit APPLY_*_FILTER / OPEN_*_DETAIL).
      const updated: CardsMessage = { ...prevCard, records: recs, messageId: messageId ?? prevCard.messageId };
      return [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)];
    });
  }, [dispatch, fetchModuleRows, setMessages, messages]);

  // "View" on an inline action card: (re-)open the workspace panel for its module + filter.
  const openActionView = useCallback(async (module: SamModule, filter: Rec, title?: string, caption?: string, recordId?: string | null) => {
    lastFilterRef.current[module] = filter;
    const surfaceSeq = ++surfaceSeqRef.current;
    const rows = await fetchModuleRows(module, filter);
    if (surfaceSeq !== surfaceSeqRef.current) return;
    renderModuleCards(module, rows, filter, undefined, title, caption);
    pushHistory(module, filter, title, caption, undefined, recordId, undefined, rows);
    if (!isDesktop) setChatOn(false);
  }, [fetchModuleRows, renderModuleCards, pushHistory, isDesktop, setChatOn]);

  // Reload restore: loadSession drops a placeholder cards message (hydrate=true) for the most
  // recent workspace view; fetch its rows here so the panel comes back from the DB-persisted view.
  // hydrateCard is memoized so its identity only changes when the actual placeholder element
  // changes — not on every socket-driven setMessages call — preventing constant fetch cancellation.
  const hydrateCard = useMemo(
    () => messages.find((m): m is CardsMessage => m.kind === 'cards' && !!(m as CardsMessage).hydrate),
    [messages],
  );
  useEffect(() => {
    if (!hydrateCard) return;
    let cancelled = false;
    (async () => {
      const f = (hydrateCard.filter || {}) as Rec;
      lastFilterRef.current[hydrateCard.module] = f;
      if (hydrateCard.hydrateRecordId) {
        // Same mechanism replayView uses to reopen a record's detail over its list.
        setOpenCards(prev => ({ ...prev, [hydrateCard.module]: hydrateCard.hydrateRecordId! }));
        setOpenCardSource(hydrateCard.module, 'recent');
      }
      const rows = await fetchModuleRows(hydrateCard.module, f);
      if (!cancelled) renderModuleCards(hydrateCard.module, rows, f, undefined, hydrateCard.title, hydrateCard.caption);
    })();
    return () => { cancelled = true; };
  }, [hydrateCard, fetchModuleRows, renderModuleCards, setOpenCardSource]);

  // A confirmation card is a one-shot socket push: one that arrived while this page was reloading,
  // on another conversation, or while the socket was down would be lost and its tool call left
  // waiting until the backend gives up. On every (re)connect and conversation switch, ask the
  // backend to send this conversation's waiting cards again (onConfirmation de-duplicates them).
  const socketConnected = useSocketStatus();
  useEffect(() => {
    if (!socketConnected || !conversationId) return;
    socketService.emit('message', { type: 'sam.confirmations_sync', conversation_id: conversationId });
  }, [socketConnected, conversationId]);

  useSamActions({
    conversationId,
    // Card-rulebook toast (SHOW_FEEDBACK) → the app-wide toaster (several may stack).
    onFeedback: (fb) => dispatch(pushToast(fb)),
    onCardEvents,
    routeCardEvents: cardEventRouting,
    cardCatalogue,
    onConfirmation: (action, presentation) => {
      setMessages(prev => {
        // The same card can arrive again — the backend re-sends waiting cards after a reload or
        // reconnect (sam.confirmations_sync). Refresh it in place, keeping any decision already made,
        // instead of appending a duplicate with the same id (and React key).
        const id = `cf-${action.call_id || Date.now()}`;
        if (action.call_id && prev.some(m => m.id === id)) {
          return prev.map(m => (m.id === id ? { ...m, action, presentation } as ConfirmMessage : m));
        }
        return [...prev, { kind: 'confirm', id, action, presentation } as ConfirmMessage];
      });
      // A panel confirmation lives in the workspace only (see ChatPage's surfacePanel), same as every
      // other surface upsert above — auto-switch off the thread on mobile so the user doesn't have to
      // tap the chat/cards toggle to see it. An OVERLAY one is a dialog over whatever is showing.
      if (!isDesktop && !isOverlayConfirm({ presentation } as ConfirmMessage)) setChatOn(false);
    },
    onConfirmationResult: (action) => {
      if (!action.call_id) return;
      setMessages(prev => prev.map(m =>
        m.kind === 'confirm' && m.action.call_id === action.call_id
          ? {
            ...m,
            // A result can arrive without a click on THIS card — the backend timed out, or the
            // decision came from another tab / device. Mark it decided too, so its buttons are
            // replaced by the result instead of staying live for a call that is already over.
            resolved: m.resolved ?? (action.status === 'cancelled' ? 'rejected' : 'approved'),
            outcome: action.status, outcomeMessage: action.status_message, outcomeData: action.data ?? null,
            // Confirmed with edits: the backend sends the details rebuilt from the user's values.
            ...(action.tool_args ? { action: { ...m.action, tool_args: action.tool_args } } : {}),
          } as ConfirmMessage
          : m,
      ));
    },
    onApplyFilter: async (module, filter, facets, title, caption, messageId, recordId, view) => {
      const f = (filter || {}) as Rec;
      lastFilterRef.current[module] = f;
      if (module === 'meetings') syncMeetingFilter(f);
      // Calendar manages its own date-range fetch inside CalendarPage — no REST rows needed here.
      const surfaceSeq = ++surfaceSeqRef.current;
      const rows = module === 'calendar' ? [] : await fetchModuleRows(module, f);
      if (surfaceSeq !== surfaceSeqRef.current) return;
      // A NEW view: with no agent title, name it from its own filter — renderModuleCards keeps the
      // previous card's title when given none (right for refreshes), which left e.g. "My High
      // Priority Tasks" over a list SAM had just narrowed to the 2 tasks it created.
      renderModuleCards(module, rows, f, facets, title ?? viewLabel(module, f), caption, messageId);
      pushHistory(module, f, title, caption, messageId, recordId, view, rows);
    },
    onRefresh: (module, messageId) => refreshModuleCard(module, messageId),
    onActionCard: (module, filter, title, caption, recordId) => {
      // Workspace display is mandatory (handled by onApplyFilter); the inline card is opt-in.
      if (!inlineActionCards) return;
      setMessages(prev => [...prev, {
        kind: 'action', id: `act-${Date.now()}-${module}`,
        module, filter: (filter || {}) as Rec, title, caption, recordId,
      } as ActionCardMessage]);
    },
    onOpenCard: (module, recordId, index, filter) => {
      if (!module) {
        setOpenCards(prev => { const n = { ...prev }; for (const k of Object.keys(n) as SamModule[]) n[k] = null; return n; });
        setOpenCardSources({});
        return;
      }
      let id = recordId;
      if (!id && index !== undefined) { const r = (cardRecordsRef.current[module] || [])[index]; if (r) id = cardId(module, r); }
      // Resolve once, reused for both the "⋯" menu entry below and SAM's own screen-awareness
      // note further down (the interactive `opened_title` patch) — previously computed
      // independently in both places, so the awareness note used the plain title while the menu
      // used the engagement's property address for the same open event.
      const rec = id ? (cardRecordsRef.current[module] || []).find(r => cardId(module, r) === id) : undefined;
      const menuTitle = rec
        ? (module === 'engagements' ? sv(rec.property_name) || cardInfo(module, rec).title : cardInfo(module, rec).title)
        : viewLabel(module, filter ?? {});
      // Record this SAM-initiated single-record open as its own "⋯" menu entry — the counterpart
      // to openCardByUser's recordView call for manual opens. Without this, asking SAM to open a
      // specific record left no trace in the session-history menu at all.
      //
      // `viewParams: filter` matters when this call is the OPEN_*_DETAIL half of a combined
      // "list + detail in one envelope" write (create_task/create_meeting/…, which also fires
      // APPLY_*_FILTER for the same record — see pushHistory below): both calls must key on the
      // SAME (cardType, contextRef, viewParams) or they record two separate menu rows instead of
      // one. Without it, this call's empty viewParams produced a distinct, filter-less key from
      // pushHistory's — a bare "Tasks" entry alongside the correctly-titled one for the record
      // just created.
      if (id) {
        recordView({ cardType: module, title: menuTitle, contextRef: contextRefFor(module, id), viewParams: filter });
      }
      // When SAM opens a property panel, synchronously strip initial_engagement_id from the
      // cards filter. onApplyFilter (which sets the correct filter) is async — it fetches rows
      // before calling renderModuleCards, so it races against this synchronous setOpenCards.
      // If a stale initial_engagement_id is still in cards.filter when WorkspacePropertyDetail
      // remounts (new key), the previous property's engagement bleeds into the new view.
      if (module === 'properties' && id) {
        setMessages(prev => prev.map(m => {
          if (m.kind !== 'cards' || (m as CardsMessage).module !== 'properties') return m;
          const cm = m as CardsMessage;
          if (!cm.filter?.initial_engagement_id) return m;
          const { initial_engagement_id: _removed, ...rest } = cm.filter as Record<string, unknown>;
          return { ...cm, filter: rest };
        }));
      }
      setOpenCards(prev => ({ ...prev, [module]: id ?? null }));
      // When opening a task/meeting/note detail, bring that module's card panel to the front so the
      // workspace switches to it (e.g. from my_todos → tasks when SAM opens a specific task, or
      // tasks → note for open_note — the note detail renders inside the notes panel).
      // If the module has no card yet (first time), fetch and create one.
      if (id && (module === 'tasks' || module === 'meetings' || module === 'notes')) {
        setMessages(prev => {
          const idx = prev.findIndex(m => m.kind === 'cards' && (m as CardsMessage).module === module);
          if (idx === -1) {
            // No card exists yet — asynchronously fetch rows and create the panel.
            const f = (lastFilterRef.current[module] ?? {}) as Rec;
            fetchModuleRows(module, f).then(recs => renderModuleCards(module, recs, f));
            return prev;
          }
          const lastCardsIdx = prev.reduce((last, m, i) => m.kind === 'cards' ? i : last, -1);
          if (idx === lastCardsIdx) return prev; // already the latest card
          const card = prev[idx];
          return [...prev.slice(0, idx), ...prev.slice(idx + 1), card];
        });
      }
      // onOpenCard is only called from SAM socket events (user clicks go directly to setOpenCards),
      // so any open here is agent-initiated → hide the back button.
      if (id) setOpenCardSource(module, 'agent');
      else setOpenCardSources(prev => { const n = { ...prev }; delete n[module]; return n; });
      // active_task/active_meeting are reported by TaskCard/WorkspaceMeetingCard via
      // useSamSurfaceContext on their next render — no bridge needed, and a direct emit here
      // would steal ownership from the card's useId, causing its own null-clear on unmount
      // to be rejected and leaving the surface stale in ui_context.
      // Push open/close state to agent context so the next turn knows what's currently open.
      socketService.emit('message', {
        type: 'agent.context_update', conversation_id: conversationId,
        context: {
          listed_module: module, opened: id ?? null, available_actions: moduleToolsRef.current[module] ?? [],
          ...cardContextPatch(module, id ?? null),
        },
      });
      emitPresentation(module, id ?? null);
      // A live interactive session doesn't rebuild its prompt per turn (unlike text mode), so it
      // never learns a detail card opened/closed unless nudged here — e.g. right after create_task
      // or open_task auto-opens the new record. Without this, "reassign it" mid-voice-session has
      // no record to resolve to until the user says something else first.
      if (mode === 'interactive') {
        socketService.emit('message', {
          type: 'agent_realtime.session.update', session_id: conversationId,
          context_patch: {
            listed_module: module, opened: id ?? null,
            opened_title: id ? menuTitle : undefined,
          },
        });
      }
      // Focusing a user card makes it the rename target.
      if (module === 'users') {
        const rec = (cardRecordsRef.current['users'] || []).find(r => cardId('users', r) === id) || null;
        selectUser(rec);
      }
      // Focusing a note in the notes LIST surfaces it to SAM as the active note. Only push when we
      // actually have that row — never clear here, or a supervisor update_note (which also fires
      // OPEN_NOTE_DETAIL) would wipe the active_note set by the capture card mid-edit.
      if (module === 'notes' && id) {
        const rec = (cardRecordsRef.current['notes'] || []).find(r => cardId('notes', r) === id) || null;
        if (rec) emitNoteContext(rec);
      }
      setTimeout(() => threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight }), 80);
    },
    onNavigateRoute: (path) => navigate(path),
    onRefreshTaskComments: (taskId) => dispatch(workspaceApi.util.invalidateTags([{ type: 'Task' as const, id: taskId }])),
    // Invalidate both the specific-record tag (refreshes an open Task Detail) AND the untargeted
    // 'Task' tag, which RTK Query also matches against the Tasks list's {type:'Task', id:'LIST'}
    // tag — same pairing updateTask's own mutation uses (workspaceApiExtensions.ts) — otherwise a
    // priority/status change made via SAM chat never refetches an open Tasks list.
    onRefreshTaskDetail: (taskId) => dispatch(workspaceApi.util.invalidateTags([{ type: 'Task' as const, id: taskId }, 'Task' as const, 'Reminder' as never])),
    // reschedule_meeting/cancel_meeting/add_meeting_note/set_meeting_reminder hit their services
    // directly from the backend (bypassing the frontend's own RTK mutations), so nothing else
    // invalidates the open meeting detail's cache — getMeetingById/getMeetingNotes/
    // getMeetingActivity all key off the same {type:'Meeting', id} tag, so one invalidation covers
    // the detail view + its sub-tabs. Also invalidate 'Reminder' (untagged by meeting id, same as
    // onRefreshTaskDetail) so set_meeting_reminder's reminder shows up without a manual reload.
    onRefreshMeetingDetail: (meetingId) => dispatch(workspaceApi.util.invalidateTags([{ type: 'Meeting' as const, id: meetingId }, 'Reminder' as never])),
    // execute_workflow_transition hits WorkflowService directly (bypassing the frontend's own
    // property mutations), so nothing else invalidates an open property detail's cache after its
    // active engagement's stage/status changes — getPropertyDetail keys off {type:'Property', id},
    // same tag WorkspacePropertyDetail's useGetPropertyDetailQuery provides.
    onRefreshPropertyDetail: (propertyId) => dispatch(workspaceApi.util.invalidateTags([{ type: 'Property' as const, id: propertyId }])),
    // SAM-driven calendar navigation (navigate_calendar tool): move/reframe the calendar panel.
    onNavigateCalendar: ({ direction, unit, view, date }) =>
      dispatch(navigateCalendarAction({ direction, unit, view, date })),
    // SAM-driven view control (switch_view tool): flip the mobile chat ↔ workspace surface.
    onSwitchView: (target) => setChatOn(target === 'chat'),
    // SAM-driven hands-free swipe (navigate_cards tool): step ±1 or jump to a 1-based position.
    // Also resolves and opens the targeted row's detail view directly — navigate_cards is the
    // tool SAM uses for "open the Nth one" against a plain list card (e.g. engagement.list), not
    // only the mobile hands-free carousel, so this must work against both surfaces.
    // Counts the rows the user SEES: a self-fetching list (the engagement list) publishes them via
    // visibleListRows, since its rows can differ from the tool payload in latestCards.records.
    onNavigateCards: ({ direction, position }) => {
      const module = latestCards?.module;
      if (!module) return 'no_list_on_screen';
      const rows: VisibleRow[] = getVisibleRows(module)
        ?? (latestCards?.records ?? []).map(r => ({ id: cardId(module, r) }));
      const total = rows.length;
      if (total <= 0) return 'no_list_on_screen';
      if (position != null && (position < 1 || position > total)) return 'position_out_of_range';
      // Computed here, not inside a setHfIndex updater: React may run the updater later, after
      // the row below was already read (that opened row 1 for every request).
      const idx = Math.max(0, Math.min(total - 1, position != null ? position - 1 : hfIndex + (direction === 'prev' ? -1 : 1)));
      setHfIndex(idx);
      const row = rows[idx];
      if (row.engagementId) onPickEngagement?.({ propertyId: row.id, engagementId: row.engagementId });
      setOpenCards(prev => ({ ...prev, [module]: row.id }));
      openCardByUser(module, row.id);
    },
    // SAM start_note_taker — open the note-capture card in the workspace (recording auto-starts in
    // NotePanel); ChatPage brings the workspace surface into view.
    onOpenNoteTaker: (opts) => { upsertNoteCard(opts?.contextType, opts?.entityId); onOpenNoteTaker?.(opts); },
    // SAM stop_note_taker — stop the on-screen recording.
    onStopNoteTaker: () => requestStopNote(),
    // SAM execute_note_followups — batch-create all selected follow-ups on the open note review card.
    onExecuteNoteFollowups: () => onExecuteNoteFollowups?.(),
    // SAM set_note_followup_included — check/uncheck one, several, or all follow-up suggestions.
    onSetNoteFollowupIncluded: (opts) => onSetNoteFollowupIncluded?.(opts),
    // SAM open_note_followup — expand a follow-up's review/edit panel, optionally prefilling fields.
    onExpandNoteFollowup: (opts) => onExpandNoteFollowup?.(opts),
    // SAM drafted (or live-edited) an email/SMS/in-app message — render it as a review-before-send
    // draft card in the WORKSPACE (where list cards render), not the chat thread. There is at most
    // one active draft surface: a re-published DRAFT_COMMUNICATION (a SAM live edit) upserts the same
    // card in place with a bumped `rev` so the card re-seeds from the fresh payload. Mobile flips to
    // the card surface so the draft is actually visible.
    onDraftCommunication: (action, messageId) => {
      const d = action.draft;
      if (!d && !action.record_id) return;
      const commId = action.record_id ?? undefined;
      const seed: ComposeDraftInput | undefined = d
        ? { channel: d.channel, recipients: d.recipients, subject: d.subject, body: d.body, context: d.context }
        : undefined;
      // record_id = the persisted draft's communication_id — the card binds to the server record so
      // it survives reload; the payload is the fallback / live re-seed source (SAM edit re-publish).
      upsertDraftCard(commId, seed, messageId);
      // List the draft in the "⋯" session menu (card/UI mode) so the user can re-open it later.
      pushDraftHistory(commId, action.title, action.caption);
      if (d) emitDraftContext({ communication_id: commId, channel: d.channel, subject: d.subject, body: d.body, recipients: d.recipients });
    },
    // QUICK_QUOTE is no longer used — all Quick Quotes open via EXPAND_ENGAGEMENT_QUICK_QUOTE
    // inside the WorkflowCard. If a stale QUICK_QUOTE action arrives (e.g. from cache), redirect
    // it to the engagement path when an engagement_id is available; otherwise ignore.
    onQuickQuote: (a) => {
      if (a.engagement_id) setExpandQuickQuoteEngagementId(a.engagement_id);
    },
    onExpandEngagementQuickQuote: (engagementId) => {
      setExpandQuickQuoteEngagementId(engagementId);
    },
    onRefreshQuickQuote: (engagementId) => {
      dispatch(quickQuotesApi.util.invalidateTags([{ type: 'QuickQuote' as const, id: engagementId }]));
      setExpandQuickQuoteEngagementId(engagementId);
      setQuickQuoteScrollTick((t) => t + 1);
    },
    onExpandEngagementTransitionCapture: (engagementId, transitionCode) => {
      setExpandTransitionEngagementId(engagementId);
      setExpandTransitionCode(transitionCode);
    },
    onOpenQuickQuoteMap: (engagementId) => {
      setExpandQuickQuoteEngagementId(engagementId);
      setOpenQuickQuoteMapEngagementId(engagementId);
    },
    onExpandEngagementAssessmentCapture: (engagementId) => {
      setExpandAssessmentCaptureEngagementId(engagementId);
      setExpandAssessmentCaptureTick((t) => t + 1);
    },
    // SAM opened (or re-pointed) the Workflow surface — get_engagement_workflow_state /
    // create_engagement bind it to an engagement; list_pending_approvals leaves it unbound
    // (approvals-only view). Render it in the WORKSPACE (where list/draft/note cards render), not
    // the chat thread — mobile flips to the card surface so it's actually visible.
    onOpenWorkflowPanel: (engagementId, title, caption, messageId) => {
      upsertWorkflowCard(engagementId, messageId);
      pushWorkflowHistory(engagementId, title, caption);
      // upsertWorkflowCard renders nothing for an engagement-bound open (engagements open inside
      // their property card) — say so in the card trace instead of failing silently.
      return engagementId ? 'engagement_opens_via_property' : undefined;
    },
    // SAM executed a transition or decided an approval — invalidate the open surface's cache so it
    // refetches (those tools hit the workflow service directly, bypassing our RTK mutations).
    onRefreshWorkflow: (engagementId) => refreshWorkflowCard(engagementId),
    // SAM opened the Company Processes list card (list_company_workflows) — the overview step
    // before the user clicks "Customize" to open the full WorkflowSettingsCard.
    onOpenCompanyWorkflowsPanel: (title, caption, messageId) => {
      upsertWorkflowListCard(messageId);
      pushWorkflowListHistory(title, caption);
    },
    // get_current_proposal/toggle_proposal_section/set_recommended_proposal_option/
    // set_proposal_option_included all bump this same signal via their shared _focus_proposal()
    // helper — and the mutating ones among them hit ProposalService directly, bypassing our RTK
    // mutations (useSetOptionIncludedMutation etc.), so the mounted useGetCurrentProposalQuery
    // never learns the data changed. Same bug class as onRefreshWorkflow below; invalidate here
    // so the card actually shows the new state instead of silently staying stale.
    onOpenProposalPreparedView: (engagementId, view) => {
      dispatch(proposalsApi.util.invalidateTags([{ type: 'Proposal' as const, id: engagementId }]));
      setProposalPreparedViewRequest((prev) => ({ engagementId, view, tick: (prev?.tick ?? 0) + 1 }));
    },
    onProposalSurfaceInteract: (payload) => {
      setProposalSurfaceInteractRequest((prev) => ({ ...payload, tick: (prev?.tick ?? 0) + 1 }));
    },
    // SAM reopened a "⋯" recent-views entry (list_recent_views / open_recent_view) — reuses
    // replayView exactly as a user's own menu click does, from the module/filter/record the
    // backend read off the stored row. `cardDefinitionId` is left blank: it's identity-irrelevant (excluded
    // from recentViewKey) and this entry already exists, so there's nothing here to distinguish.
    onReplayRecentView: (module, filter, recordId, title, caption, contextRefType) => {
      const contextRef = contextRefType
        ? { type: contextRefType, id: recordId || '' }
        : contextRefFor(module, recordId);
      void replayView({
        key: recentViewKey({ cardType: module, contextRef, viewParams: filter }),
        cardType: module,
        cardDefinitionId: '',
        title: title || '',
        subtitle: caption,
        contextRef,
        viewParams: filter,
        source: 'replay',
        lastAccessedAt: Date.now(),
      });
    },
    // SAM opened (or re-opened) the Workflow SETTINGS surface (get_workflow_customization) — a
    // SECOND, DISTINCT surface from the engagement one above (no engagement scope). Render it in
    // the WORKSPACE, same as the engagement surface — mobile flips to the card surface so it's
    // actually visible.
    onOpenWorkflowSettingsPanel: (title, caption, messageId) => {
      upsertWorkflowSettingsCard(messageId);
      pushWorkflowSettingsHistory(title, caption);
    },
    // SAM toggled/renamed/added a structure item or set a transition's authority — invalidate the
    // open settings surface's cache so it refetches (those tools hit the customization service
    // directly, bypassing our RTK mutations).
    onRefreshWorkflowSettings: () => refreshWorkflowSettingsCard(),
  });

  // Build a structured "what cards are visible" view from the latest cards message. ChatPage owns
  // visibility (mobile shows chat OR cards; desktop shows both), so it decides whether this surface
  // is actually on screen and registers it. The agent.context_update emits in renderModuleCards stay.
  const latestCards = useMemo(
    () => [...messages].reverse().find((m): m is CardsMessage => m.kind === 'cards') ?? null,
    [messages],
  );
  const latestDraft = useMemo(
    () => [...messages].reverse().find((m): m is DraftCardMessage => m.kind === 'draftcard') ?? null,
    [messages],
  );
  const latestNote = useMemo(
    () => [...messages].reverse().find((m): m is NoteCardMessage => m.kind === 'notecard') ?? null,
    [messages],
  );
  const latestWorkflow = useMemo(
    () => [...messages].reverse().find((m): m is WorkflowCardMessage => m.kind === 'workflowcard') ?? null,
    [messages],
  );
  // A confirmation card the user hasn't decided on yet. It wins the workspace whatever arrives
  // after it (an async list fetch, a draft, a note, a workflow card) — the tool call is blocked on
  // it, and burying it left nothing to click until the backend gave up.
  const pendingConfirm = useMemo(
    () => [...messages].reverse().find(
      (m): m is ConfirmMessage => m.kind === 'confirm' && !m.resolved && !m.dismissed && !isOverlayConfirm(m),
    ) ?? null,
    [messages],
  );
  // Shown as a dialog over the page instead (rulebook OVERLAY), until the user closes its result.
  const overlayConfirm = useMemo(
    () => [...messages].reverse().find(
      (m): m is ConfirmMessage => m.kind === 'confirm' && !m.dismissed && isOverlayConfirm(m),
    ) ?? null,
    [messages],
  );
  // The single most-recent workspace surface: a pending confirmation first; otherwise the latest of
  // cards/draft/note/workflow and a DECIDED confirmation (not dismissed) — a decided card stays up
  // showing its result until the user closes it or a newer surface replaces it.
  const latestSurface = useMemo(
    () => pendingConfirm ?? [...messages].reverse().find(
      (m): m is CardsMessage | DraftCardMessage | NoteCardMessage | WorkflowCardMessage | ConfirmMessage =>
        m.kind === 'cards' || m.kind === 'draftcard' || m.kind === 'notecard' || m.kind === 'workflowcard' ||
        (m.kind === 'confirm' && !m.dismissed && !isOverlayConfirm(m)),
    ) ?? null,
    [messages, pendingConfirm],
  );
  const latestConfirm = latestSurface?.kind === 'confirm' ? latestSurface : null;
  const workspaceView = useMemo<WorkspaceView | null>(() => {
    if (!latestSurface) return null;
    // Notifications overlay is visible — report empty notifications surface so SAM doesn't
    // see stale module items (e.g. properties list) from the workspace behind the panel.
    if (openNotificationsPanel) {
      return { kind: 'workspace', module: 'notifications', filter: {}, title: 'Notifications', items: [], openCardId: null };
    }
    if (latestSurface.kind === 'confirm') {
      // Nothing structured to report to SAM here — it already knows what it just asked for.
      // While this is null, `latestSurface`/`showWorkspace` still keep the confirm card itself on
      // screen (ChatPage renders off latestSurface.kind, not off this memo).
      return null;
    }
    if (latestSurface.kind === 'draftcard') {
      // A draft is a single editable object — surface it as one synthetic communications card so the
      // agent knows a draft is on screen (its live field values ride the separate active_draft patch).
      const title = latestSurface.draft?.subject || 'Draft';
      return {
        kind: 'workspace', module: 'communications', filter: {}, title: 'Draft',
        items: latestSurface.communication_id ? [{ index: 1, id: latestSurface.communication_id, title }] : [],
        openCardId: null,
      };
    }
    if (latestSurface.kind === 'notecard') {
      // A note capture/review card is on screen — its live content rides the separate active_note
      // patch, so surface it as a single synthetic notes card just to mark the module in view.
      return { kind: 'workspace', module: 'notes', filter: {}, title: 'Note Taker', items: [], openCardId: null };
    }
    if (latestSurface.kind === 'workflowcard') {
      // A Workflow surface is on screen — its live content rides the separate
      // active_engagement_workflow patch, so surface it as a single synthetic workflow card just to
      // mark the module (and bound engagement, if any) in view.
      const engagementId = latestSurface.engagementId;
      return {
        kind: 'workspace', module: 'workflow', filter: {}, title: 'Workflow',
        items: engagementId ? [{ index: 1, id: engagementId, title: 'Workflow' }] : [],
        openCardId: null,
        cardDefinitionId: latestSurface.mode === 'settings' ? 'workflow_settings.view'
          : latestSurface.mode === 'list' ? 'company_workflow.list' : 'engagement.view',
      };
    }
    const mod = latestSurface.module;
    // Mirrors renderModuleCards' item-building above (agent.context_update) — this memo feeds
    // the per-turn `meta` snapshot (buildAgentContextMeta, sent with every sam.message) AND the
    // debounced agent_realtime.state_sync push, both SEPARATE paths from renderModuleCards'
    // socket emission — all must agree on numbering or an ordinal reference resolves against
    // whichever one the model reads last (this exact drift previously caused "open the last
    // one" to silently reuse an unrelated id when only one of the two was fixed).
    const items = buildWorkspaceItems(mod, latestSurface.records as Rec[])
      .map(({ index, id, title, status }) => ({ index, id, title, status }));
    return {
      kind: 'workspace', module: mod, filter: latestSurface.filter, title: latestSurface.title,
      items, openCardId: openCards[mod] ?? null,
      availableActions: moduleTools[mod],
      cardDefinitionId: resolveCardDefinition(cardCatalogue, mod, openCards[mod] ?? null)?.id ?? latestSurface.cardDefinitionId ?? null,
    };
  }, [latestSurface, openCards, openNotificationsPanel, moduleTools, cardCatalogue]);

  // When the interactive session starts, re-push the current workspace card context so the new
  // realtime session sees what's already on screen (without this, a fresh session that starts
  // while cards are visible gets no [SCREEN UPDATE] until the next user-initiated navigation).
  useEffect(() => {
    if (mode !== 'interactive' || !conversationId || !latestCards) return;
    const mod = latestCards.module;
    if (!mod) return;
    const openId = openCards[mod] ?? null;
    const recs = cardRecordsRef.current[mod] || [];
    const rec = openId ? recs.find(r => cardId(mod, r) === openId) : undefined;
    const title = rec ? cardInfo(mod, rec).title : undefined;
    socketService.emit('message', {
      type: 'agent_realtime.session.update', session_id: conversationId,
      context_patch: { listed_module: mod, opened: openId, opened_title: title ?? undefined },
    });
  }, [mode, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

return {
  openCards,
  setOpenCards,
  openCardSources,
  replayView,
  openActionView,
  workspaceView,
  selectUser,
  latestCards,
  latestDraft,
  latestNote,
  latestWorkflow,
  latestConfirm,
  dismissConfirm,
  overlayConfirm,
  latestSurface,
  emitDraftContext,
  emitNoteContext,
  emitSurfaceContext,
  openCardByUser,
  reportUserList,
  closeNoteCard,
  upsertNoteCard,
  refreshNotesList: () => { void refreshModuleCard('notes'); },
  refreshMyTodosList: () => { void refreshModuleCard('my_todos'); },
  openWorkflowSettings: upsertWorkflowSettingsCard,
  expandQuickQuoteEngagementId,
  quickQuoteScrollTick,
  expandTransitionEngagementId,
  expandTransitionCode,
  openQuickQuoteMapEngagementId,
  expandAssessmentCaptureEngagementId,
  expandAssessmentCaptureTick,
  proposalPreparedViewRequest,
  proposalSurfaceInteractRequest,
};
}

